// BAT-A1: bound `.in()` clause sizes for Postgres queries that fan out a
// caller-supplied key array.
//
// Postgres tolerates large IN(...) lists in principle, but at scale the
// payload size, planner cost, and protocol-level parameter binding all
// degrade — and Supabase's PostgREST surface bounces requests whose URL or
// JSON body exceeds its limit. The schedule-aware recommendation pipeline
// (useScheduleAwareRecommendations + buildTicketTrainingContextBatch) is
// the most visible offender: a busy multi-tech schedule window can pass
// 500–2000 ticket IDs into 5 sister `.in('ticket_id', ids)` reads on every
// Learning Center refresh, with no chunking guard.
//
// `chunkInQuery` splits the key array into chunks of at most `chunkSize`,
// invokes the caller-supplied query lambda in parallel per chunk, and
// concatenates the rows. It is intentionally tiny — it does not
// deduplicate, sort, reshape, or page — so it composes with any
// Supabase query builder shape (`.select()`, `.in()`, `.not()`, ordering,
// limits-per-chunk) the caller wires up inside the lambda.
//
// On the empty-keys path it short-circuits before invoking the lambda so
// callers don't need a defensive `if (keys.length === 0) return …` ahead of
// the call. On the single-chunk path it forwards the lambda's return value
// unchanged, so callers that pass a small array see no behavior change vs.
// a direct `.in()` call (no extra Promise.all wrap, no extra array copies).

/**
 * Default per-chunk cap. 500 keeps the IN(...) clause comfortably below
 * Postgres' practical limits on planner cost and URL/body payload size,
 * while still being large enough that a 500-ticket schedule window resolves
 * in a single chunk.
 */
export const DEFAULT_CHUNK_SIZE = 500;

export interface ChunkInQueryResult<TRow> {
  data: TRow[];
  /**
   * The first error reported by any chunk, or null if all chunks
   * succeeded. The surrounding caller pattern in the schedule-aware
   * recommendation pipeline treats any error as a fatal early-exit, so a
   * single-error summary is sufficient — partial-success semantics are
   * not part of the contract.
   */
  error: unknown | null;
}

export async function chunkInQuery<TKey, TRow>(
  keys: TKey[],
  // PromiseLike (not Promise): a Supabase PostgrestFilterBuilder is a thenable
  // that resolves to { data, error } but lacks .catch/.finally, so it is not a
  // `Promise`. `await` accepts any PromiseLike, so this is exact, not a widening.
  query: (chunk: TKey[]) => PromiseLike<{ data: TRow[] | null; error: unknown }>,
  chunkSize: number = DEFAULT_CHUNK_SIZE,
): Promise<ChunkInQueryResult<TRow>> {
  if (keys.length === 0) {
    // Short-circuit: no need to invoke the query. Mirrors the
    // pre-BAT-A1 callsite-level guard at buildTicketTrainingContextBatch's
    // top so existing callers see no behavior change for the empty path.
    return { data: [], error: null };
  }

  if (keys.length <= chunkSize) {
    // Single-chunk fast path: forward the lambda's result without an
    // extra Promise.all wrap or array copy. Callers that pass a typical-
    // size array (under DEFAULT_CHUNK_SIZE) get identical wire-level
    // behavior to a direct `.in()` call.
    const res = await query(keys);
    return { data: res.data ?? [], error: res.error ?? null };
  }

  const chunks: TKey[][] = [];
  for (let i = 0; i < keys.length; i += chunkSize) {
    chunks.push(keys.slice(i, i + chunkSize));
  }

  const results = await Promise.all(chunks.map((c) => query(c)));

  let firstError: unknown | null = null;
  const data: TRow[] = [];
  for (const r of results) {
    if (r.error && firstError === null) firstError = r.error;
    if (r.data) data.push(...r.data);
  }

  return { data, error: firstError };
}
